import { panelNavHtml } from '../panel-nav-html.js';

export interface DiagramNode {
  id: string;
  label: string;
  sub?: string;
}

export interface DiagramPayload {
  id: string;
  title: string;
  subtitle: string;
  nodes: DiagramNode[];
}

/**
 * Animated service flow diagram. Renders the payload's nodes as a row of
 * glass cards connected by arrows that draw in sequence. Each node fades
 * + slides in, then its outbound arrow draws to the next one. On larger
 * flows it wraps onto a second row.
 */
export class ServiceDiagram {
  private readonly root: HTMLElement;
  private timers: number[] = [];

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('diagram-overlay');
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === this.root || t.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (t.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
      }
    });
  }

  show(payload: DiagramPayload) {
    this.clearTimers();
    const nodeBlocks = payload.nodes
      .map(
        (n, i) => `
          <div class="diagram-node" data-step="${i}">
            <div class="diagram-node-index">${i + 1}</div>
            <div class="diagram-node-label">${escape(n.label)}</div>
            ${n.sub ? `<div class="diagram-node-sub">${escape(n.sub)}</div>` : ''}
          </div>
          ${
            i < payload.nodes.length - 1
              ? `<div class="diagram-arrow" data-step="${i}">
                   <svg viewBox="0 0 40 12" aria-hidden="true">
                     <defs>
                       <marker id="arrowhead-${payload.id}-${i}" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                         <path d="M0,0 L8,4 L0,8 Z" fill="currentColor"/>
                       </marker>
                     </defs>
                     <line x1="0" y1="6" x2="32" y2="6" stroke="currentColor" stroke-width="1.5" marker-end="url(#arrowhead-${payload.id}-${i})" class="diagram-arrow-line"/>
                   </svg>
                 </div>`
              : ''
          }`,
      )
      .join('');

    this.root.innerHTML = `
      <div class="diagram-sheet">
        ${panelNavHtml()}
        <div class="diagram-header">
          <div>
            <div class="diagram-eyebrow">Engagement sketch</div>
            <div class="diagram-title">${escape(payload.title)}</div>
            <div class="diagram-sub">${escape(payload.subtitle)}</div>
          </div>
        </div>
        <div class="diagram-flow" data-id="${payload.id}">
          ${nodeBlocks}
        </div>
        <div class="diagram-footnote">Illustrative. Your actual workflow will be scoped to your stack.</div>
      </div>
    `;

    this.root.classList.add('visible');

    // Staged reveal: node i+1 appears, then its inbound arrow draws.
    const nodes = this.root.querySelectorAll<HTMLElement>('.diagram-node');
    const arrows = this.root.querySelectorAll<HTMLElement>('.diagram-arrow');
    nodes.forEach((n, i) => {
      this.timers.push(
        window.setTimeout(() => n.classList.add('drawn'), 120 + i * 320),
      );
    });
    arrows.forEach((a, i) => {
      this.timers.push(
        window.setTimeout(() => a.classList.add('drawn'), 260 + i * 320),
      );
    });
  }

  hide() {
    this.root.classList.remove('visible');
    this.clearTimers();
  }

  private clearTimers() {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
